import { defineComponent as _defineComponent } from 'vue'
import { normalizeStyle as _normalizeStyle, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

import { onMounted, onUnmounted, ref, shallowRef, watch } from 'vue'
import {
  Alignment,
  Event as RiveEvent,
  EventCallback,
  Fit,
  Layout,
  Rive,
} from '@rive-app/canvas'
import { emitter } from '@/services/mitt'
import { assertNotNull } from '@/helpers/typing'

/**
 * This component is a wrapper around the Rive webplayer.
 *
 * The props in this component are a subset of the parameters in the Rive constructor
 * that we actually use. Check Rive's documentation for the complete list of parameters
 * and their descriptions: https://rive.app/docs/runtimes/web/rive-parameters
 *
 * Rive's methods and properties can be accessed through the `rive` ref, which is exposed
 * in this component. The link above also has documentation on Rive's properties and methods.
 */

export default /*@__PURE__*/_defineComponent({
  __name: 'Rive',
  props: {
    src: {},
    animations: {},
    stateMachines: {},
    autoplay: { type: Boolean, default: true },
    fit: {},
    alignment: {},
    onLoad: {},
    onLoadError: {},
    onPlay: {},
    onPause: {},
    onStop: {},
    onLoop: {},
    onStateChange: {},
    onAdvance: {}
  },
  setup(__props: any, { expose: __expose }) {

const props = __props

const canvas = ref<HTMLCanvasElement | null>(null)
const rive = shallowRef<Rive | null>(null)
const mediaQueryListener = shallowRef<MediaQueryList | null>(null)
const isLoaded = ref(false)

onMounted(async () => {
  rive.value = new Rive({
    ...props,
    canvas: assertNotNull(canvas.value),
    layout: createLayout(),
    onLoad: (event: RiveEvent): void => {
      isLoaded.value = true
      rive.value?.resetArtboardSize()
      resizeDrawings()
      props.onLoad?.(event)
    },
  })

  // Redraw the canvas when the window is resized.
  emitter.on('windowResize', resizeDrawings)

  // Redraw the canvas when the screen's dpi changes.
  mediaQueryListener.value = window.matchMedia(
    `(resolution: ${window.devicePixelRatio}dppx)`,
  )
  mediaQueryListener.value.addEventListener('change', resizeDrawings)
})

onUnmounted(() => {
  emitter.off('windowResize')
  mediaQueryListener.value?.removeEventListener('change', resizeDrawings)
  rive.value?.removeAllRiveEventListeners()
  rive.value?.cleanup()
})

// Rive isn't reactive, so we need to watch for changes
// in the props and manually update them.
watch(
  () => props.src,
  (newSrc) => {
    isLoaded.value = false
    rive.value?.load({ ...props, src: newSrc })
  },
)

watch(
  () => props.animations,
  (newAnimation) => {
    // Reset the current animation.
    if (rive.value?.isPlaying) {
      rive.value?.reset()
    }

    // Play the new animation. If it's `undefined`,
    // play the default animation.
    rive.value?.play(newAnimation || rive.value.animationNames[0])
  },
)

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
watch([() => props.fit, () => props.alignment], (): void => {
  if (rive.value) {
    rive.value.layout = assertNotNull(createLayout())
    resizeDrawings()
  }
})

function createLayout(): Layout | undefined {
  if (!props.fit && !props.alignment) {
    return
  }

  const fit = props.fit ? Fit[props.fit] : undefined
  const alignment = props.alignment ? Alignment[props.alignment] : undefined

  return new Layout({
    fit,
    alignment,
  })
}

/**
 * This is a wrapper around `rive.resizeDrawingSurfaceToCanvas`,
 * that we use so that we can have a reference to clear the event
 * listeners where we attach it.
 */
function resizeDrawings(): void {
  rive.value?.resizeDrawingSurfaceToCanvas()
}

__expose({
  rive,
  isLoaded,
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("canvas", {
    ref_key: "canvas",
    ref: canvas,
    class: "rive",
    style: _normalizeStyle({
      '--rive-width': _ctx.$attrs.width as number,
      '--rive-height': _ctx.$attrs.height as number,
    })
  }, null, 4))
}
}

})